/**
 * 拖拽吸附到画布边缘/中心/参考线的共用逻辑（「视图→对齐」，DESIGN.md
 * §6.5 补充），外加命中时的对齐提示线渲染。
 *
 * 独立成模块是因为"拖拽调整位置"在这个应用里有两条完全不同的指针
 * 处理入口——移动工具（move-tool.ts，操作已经在文档里的图层）和置入
 * 工具（place-tool.ts，操作还没写进文档、拖放图片时的临时会话）——
 * 两边都需要同一种"靠近目标就吸附+粘滞+画提示线"的手感，不应该各写一份。
 */

/** 吸附阈值，屏幕像素——跟 Photoshop"智能吸附"手感接近的量级。 */
export const SNAP_THRESHOLD_SCREEN_PX = 8;

/**
 * 已经吸附上时，松开所需的距离是刚吸附上时的这么多倍——磁性吸附的
 * "粘滞感"：普通吸附一松劲就掉，手感很轻；这里故意让已经吸附住的目标
 * 更难甩开，拖拽经过对齐位置时会先"卡"一下再滑走。数值是手感调出来的，
 * 没有更"正确"的公式依据。
 */
export const SNAP_RELEASE_MULTIPLIER = 2.5;

export interface SnapResult {
  /** 吸附后的位移；没有命中则原样等于 `raw`。 */
  readonly value: number;
  /** 命中的目标位置（文档坐标，画布边缘/中心或参考线的绝对坐标）；
   * 没命中为 null——跟 `value === raw` 不是同一件事：真的拖到刚好
   * 等于 raw 的位置也可能命中一个目标，这时候提示线该画，不能只看
   * 位移有没有变化来判断有没有吸附。 */
  readonly target: number | null;
}

/**
 * 在一维上找离 `raw` 最近、且在阈值内的吸附候选，取代 `raw`；找不到就原样返回。
 *
 * `offsets` 是内容边界上会去比对目标的几个点相对内容原点的偏移（比如左边界
 * 偏移 0、右边界偏移 width、中线偏移 width/2）；`targets` 是候选目标位置
 * （画布边缘/中心/参考线）。两者关系是 `candidate = target - offset`——
 * 因为吸附作用于位移量而不是绝对坐标：偏移量为 `offset` 的那条边要落在
 * `target` 上，需要的位移就是 `target - offset`。
 *
 * 全部 offset×target 组合摊平了一起比，而不是先按 offset 分组各选一个再在
 * "分组冠军"之间比：分组冠军里必然混着"没找到匹配、原样返回 raw"的哨兵值，
 * 它跟 raw 的距离恒为 0，会赢掉所有真正的吸附——摊平之后就没有这个哨兵值
 * 混进来的问题。
 *
 * `sticky` 非空时表示这一轴上一次已经吸附在 `sticky.target` 上——先用
 * 更宽的 `sticky.releaseThreshold` 判断是否继续锁在原目标，不管有没有
 * 别的目标离得更近：这正是"粘滞"的来源，避免同一次拖拽里在两个相近
 * 目标间来回抖。只有拖出这个更宽的范围才会松开，落回下面的正常搜索。
 */
export function bestSnap(
  raw: number,
  offsets: readonly number[],
  targets: readonly number[],
  threshold: number,
  sticky: { readonly target: number; readonly releaseThreshold: number } | null,
): SnapResult {
  if (sticky !== null) {
    let stickyValue = raw;
    let stickyDelta = Infinity;
    for (const offset of offsets) {
      const candidate = sticky.target - offset;
      const delta = Math.abs(candidate - raw);
      if (delta < stickyDelta) {
        stickyDelta = delta;
        stickyValue = candidate;
      }
    }
    if (stickyDelta < sticky.releaseThreshold) {
      return { value: stickyValue, target: sticky.target };
    }
  }

  let best = raw;
  let bestDelta = threshold;
  let matched: number | null = null;
  for (const offset of offsets) {
    for (const target of targets) {
      const candidate = target - offset;
      const delta = Math.abs(candidate - raw);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = candidate;
        matched = target;
      }
    }
  }
  return { value: best, target: matched };
}

export interface AxisSnapInput {
  /** 拖拽产生的原始位移（吸附前）。 */
  readonly raw: number;
  /** 内容边界在这一轴上的起点（文档坐标）。 */
  readonly boundsStart: number;
  /** 内容边界在这一轴上的尺寸。 */
  readonly boundsSize: number;
  /** 文档在这一轴上的尺寸——画布边缘/中心目标由此算出。 */
  readonly docSize: number;
  /** 这一轴上的参考线位置（已按 axis 筛过）。 */
  readonly guidePositions: readonly number[];
  readonly threshold: number;
  readonly releaseThreshold: number;
  /** 上一轮这一轴命中的目标，没有则传 null。 */
  readonly stickyTarget: number | null;
}

/** `bestSnap` 的单轴封装：画布边缘/中心/参考线三类目标 + 内容左边界/右边界/中线三个偏移，组好参数调用。 */
export function snapAxis(input: AxisSnapInput): SnapResult {
  const { boundsStart, boundsSize, docSize } = input;
  const targets = [0, docSize, docSize / 2, ...input.guidePositions];
  const offsets = [boundsStart, boundsStart + boundsSize, boundsStart + boundsSize / 2];
  return bestSnap(
    input.raw,
    offsets,
    targets,
    input.threshold,
    input.stickyTarget === null ? null : { target: input.stickyTarget, releaseThreshold: input.releaseThreshold },
  );
}

/**
 * 命中吸附目标时的对齐提示线（同 Photoshop 智能参考线：一条贯穿画布的
 * 实心洋红细线，不透明、不描边发光）。
 *
 * 这里原来还叠了一层 5px 宽、35% 透明度的"发光"衬底想让线更抓眼，
 * 结果是真的 bug：拖拽时哪一轴先天就贴着目标（最常见的情形——刚
 * drop 进来的图片默认左右上下都居中摆放，只要用户单纯左右拖，垂直轴
 * 从头到尾都对着画布竖直中线不放），这条线会整段贴着拖拽路径存在，
 * 5px×35% 的半透明衬底扫过去的地方，图片在那条线宽范围内的颜色肉眼
 * 可见地被泛红/泛紫——用户反馈"拖动图片后图片会变色"，根因就是这层
 * 衬底，不是真的往图片像素里写了什么。改回纯 1px 实线（不虚线、不
 * 衬底），量级上跟真实 Photoshop 的智能参考线一致：细到只在线本身
 * 经过的那一行/列像素才会跟内容混色，不会读成"一整块变色"。
 */
export function drawSnapGuideLines(
  ctx: CanvasRenderingContext2D,
  snapX: number | null,
  snapY: number | null,
  docSize: { readonly width: number; readonly height: number },
  viewportScale: number,
): void {
  if (snapX === null && snapY === null) return;
  ctx.save();
  ctx.strokeStyle = '#f024ec'; // Photoshop 智能参考线同款洋红，跟其它工具的黑白蚂蚁线/描边区分开
  ctx.lineWidth = 1 / viewportScale;
  ctx.beginPath();
  if (snapX !== null) {
    ctx.moveTo(snapX, 0);
    ctx.lineTo(snapX, docSize.height);
  }
  if (snapY !== null) {
    ctx.moveTo(0, snapY);
    ctx.lineTo(docSize.width, snapY);
  }
  ctx.stroke();
  ctx.restore();
}
